/**
 * Default category tree.
 *
 * Two-level: top-level categories with optional subcategories. Every category
 * is editable and most are deletable — these are a starting point, not a fixed
 * taxonomy. `builtin` marks the ones the app relies on existing (only
 * "uncategorized" is truly load-bearing).
 *
 * `kind` (fixed/variable) and `need` (essential/discretionary) are what make
 * the reports answer "what could I actually cut", so every default sets them
 * deliberately rather than defaulting everything to one value.
 */

import type { Category, CategoryNeed } from './types.ts';

export const UNCATEGORIZED_ID = 'cat_uncategorized';

interface CategorySeed {
  id: string;
  he: string;
  en: string;
  color: string;
  kind: Category['kind'];
  need: Category['need'];
  children?: Array<Omit<CategorySeed, 'children'>>;
}

const SEEDS: CategorySeed[] = [
  {
    id: 'cat_groceries',
    he: 'סופרמרקט ומזון',
    en: 'Groceries',
    color: '#16a34a',
    kind: 'variable',
    need: 'essential',
    children: [
      { id: 'cat_groceries_bakery', he: 'מאפייה', en: 'Bakery', color: '#22c55e', kind: 'variable', need: 'essential' },
      { id: 'cat_groceries_convenience', he: 'מכולת ונוחות', en: 'Convenience', color: '#4ade80', kind: 'variable', need: 'essential' },
    ],
  },
  {
    id: 'cat_dining',
    he: 'מסעדות ובתי קפה',
    en: 'Dining out',
    color: '#f97316',
    kind: 'variable',
    need: 'discretionary',
    children: [
      { id: 'cat_dining_delivery', he: 'משלוחים', en: 'Food delivery', color: '#fb923c', kind: 'variable', need: 'discretionary' },
      { id: 'cat_dining_cafe', he: 'בתי קפה', en: 'Cafés', color: '#fdba74', kind: 'variable', need: 'discretionary' },
    ],
  },
  {
    id: 'cat_transport',
    he: 'תחבורה',
    en: 'Transport',
    color: '#0ea5e9',
    kind: 'variable',
    need: 'essential',
    children: [
      { id: 'cat_transport_fuel', he: 'דלק', en: 'Fuel', color: '#38bdf8', kind: 'variable', need: 'essential' },
      { id: 'cat_transport_ev', he: 'טעינת רכב חשמלי', en: 'EV charging', color: '#7dd3fc', kind: 'variable', need: 'essential' },
      { id: 'cat_transport_public', he: 'תחבורה ציבורית', en: 'Public transport', color: '#0284c7', kind: 'variable', need: 'essential' },
      { id: 'cat_transport_tolls', he: 'כבישי אגרה וחניה', en: 'Tolls & parking', color: '#0369a1', kind: 'variable', need: 'essential' },
    ],
  },
  {
    id: 'cat_utilities',
    he: 'חשבונות ותשתיות',
    en: 'Utilities',
    color: '#8b5cf6',
    kind: 'fixed',
    need: 'essential',
    children: [
      { id: 'cat_utilities_electricity', he: 'חשמל', en: 'Electricity', color: '#a78bfa', kind: 'fixed', need: 'essential' },
      { id: 'cat_utilities_water', he: 'מים', en: 'Water', color: '#c4b5fd', kind: 'fixed', need: 'essential' },
      { id: 'cat_utilities_telecom', he: 'תקשורת ואינטרנט', en: 'Telecom', color: '#7c3aed', kind: 'fixed', need: 'essential' },
    ],
  },
  {
    id: 'cat_insurance',
    he: 'ביטוח',
    en: 'Insurance',
    color: '#64748b',
    kind: 'fixed',
    need: 'essential',
  },
  {
    id: 'cat_health',
    he: 'בריאות',
    en: 'Health',
    color: '#ef4444',
    kind: 'variable',
    need: 'essential',
    children: [
      { id: 'cat_health_pharmacy', he: 'בית מרקחת', en: 'Pharmacy', color: '#f87171', kind: 'variable', need: 'essential' },
      { id: 'cat_health_fitness', he: 'כושר וספורט', en: 'Fitness', color: '#fca5a5', kind: 'fixed', need: 'discretionary' },
    ],
  },
  {
    id: 'cat_travel',
    he: 'נסיעות וחופשות',
    en: 'Travel',
    color: '#06b6d4',
    kind: 'variable',
    need: 'discretionary',
    children: [
      { id: 'cat_travel_flights', he: 'טיסות', en: 'Flights', color: '#22d3ee', kind: 'variable', need: 'discretionary' },
      { id: 'cat_travel_hotels', he: 'מלונות ולינה', en: 'Hotels', color: '#67e8f9', kind: 'variable', need: 'discretionary' },
      { id: 'cat_travel_car_rental', he: 'השכרת רכב', en: 'Car rental', color: '#0891b2', kind: 'variable', need: 'discretionary' },
      { id: 'cat_travel_dining', he: 'אוכל בחו"ל', en: 'Dining abroad', color: '#a5f3fc', kind: 'variable', need: 'discretionary' },
    ],
  },
  {
    id: 'cat_shopping',
    he: 'קניות',
    en: 'Shopping',
    color: '#ec4899',
    kind: 'variable',
    need: 'discretionary',
    children: [
      { id: 'cat_shopping_clothing', he: 'ביגוד', en: 'Clothing', color: '#f472b6', kind: 'variable', need: 'discretionary' },
      { id: 'cat_shopping_electronics', he: 'אלקטרוניקה', en: 'Electronics', color: '#f9a8d4', kind: 'variable', need: 'discretionary' },
    ],
  },
  {
    id: 'cat_entertainment',
    he: 'פנאי ובידור',
    en: 'Entertainment',
    color: '#eab308',
    kind: 'variable',
    need: 'discretionary',
    children: [
      { id: 'cat_entertainment_subscriptions', he: 'מנויים דיגיטליים', en: 'Subscriptions', color: '#facc15', kind: 'fixed', need: 'discretionary' },
    ],
  },
  {
    id: 'cat_home',
    he: 'בית ומשק בית',
    en: 'Home',
    color: '#a16207',
    kind: 'variable',
    need: 'essential',
  },
  {
    id: 'cat_education',
    he: 'חינוך',
    en: 'Education',
    color: '#2563eb',
    kind: 'fixed',
    need: 'essential',
  },
  {
    id: 'cat_government',
    he: 'ממשלה ומיסים',
    en: 'Government & tax',
    color: '#475569',
    kind: 'fixed',
    need: 'essential',
  },
  {
    id: 'cat_other',
    he: 'אחר',
    en: 'Other',
    color: '#94a3b8',
    kind: 'variable',
    need: 'discretionary',
  },
];

export function defaultCategories(): Category[] {
  const categories: Category[] = [];
  let order = 0;

  for (const seed of SEEDS) {
    categories.push({
      id: seed.id,
      parentId: null,
      labelHe: seed.he,
      labelEn: seed.en,
      color: seed.color,
      kind: seed.kind,
      need: seed.need,
      order: order++,
      builtin: true,
    });

    for (const child of seed.children ?? []) {
      categories.push({
        id: child.id,
        parentId: seed.id,
        labelHe: child.he,
        labelEn: child.en,
        color: child.color,
        kind: child.kind,
        need: child.need,
        order: order++,
        builtin: true,
      });
    }
  }

  return categories;
}

/** Top-level categories, ordered. */
export function rootCategories(categories: readonly Category[]): Category[] {
  return categories.filter((c) => c.parentId === null).sort((a, b) => a.order - b.order);
}

export function childrenOf(categories: readonly Category[], parentId: string): Category[] {
  return categories.filter((c) => c.parentId === parentId).sort((a, b) => a.order - b.order);
}

/**
 * Every descendant of a category, including itself. Used when filtering by a
 * parent category should also include everything filed under its children.
 */
export function categoryWithDescendants(
  categories: readonly Category[],
  id: string,
): Set<string> {
  const ids = new Set([id]);
  for (const category of categories) {
    if (category.parentId !== null && ids.has(category.parentId)) ids.add(category.id);
  }
  return ids;
}

/**
 * `categoryWithDescendants` over several ids at once, unioned.
 *
 * An empty input gives an empty set, which callers read as "no filter" rather
 * than "match nothing" — the same convention the month filter uses.
 */
export function categoriesWithDescendants(
  categories: readonly Category[],
  ids: ReadonlySet<string>,
): Set<string> {
  const all = new Set<string>();
  for (const id of ids) {
    for (const descendant of categoryWithDescendants(categories, id)) all.add(descendant);
  }
  return all;
}

/**
 * Whether a row falls inside a set from `categoryWithDescendants`.
 *
 * Either field can match, and that asymmetry is the whole point. A row filed
 * under a subcategory carries the parent in `categoryId` and the child in
 * `subcategoryId`, so selecting a parent catches both the rows filed directly
 * on it and the rows filed under any of its children, while selecting a child
 * catches only that child — the parent is not in the child's descendant set.
 */
export function inCategorySet(
  ids: ReadonlySet<string>,
  categoryId: string | null,
  subcategoryId: string | null,
): boolean {
  if (categoryId !== null && ids.has(categoryId)) return true;
  return subcategoryId !== null && ids.has(subcategoryId);
}

/**
 * Whether a row's spend is essential or discretionary. `null` when it has no
 * category, or one that no longer exists.
 *
 * **The subcategory wins, and it is not the same as rolling up to the parent.**
 * A child may deliberately disagree with its parent: the shipped tree files
 * "כושר וספורט" as discretionary underneath an essential "בריאות", and
 * "אוכל בחו״ל" as discretionary under travel. Resolving through the root the
 * way `totalsByCategory` does would quietly reclassify exactly the rows this
 * distinction exists to separate.
 */
export function resolveNeed(
  byId: ReadonlyMap<string, Category>,
  categoryId: string | null,
  subcategoryId: string | null,
): CategoryNeed | null {
  const leaf = subcategoryId === null ? undefined : byId.get(subcategoryId);
  if (leaf !== undefined) return leaf.need;
  const root = categoryId === null ? undefined : byId.get(categoryId);
  return root?.need ?? null;
}
